import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { Separator } from '@1trading/react/components/ui/separator';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import LockedAnswersNotice from '@/components/ui/locked-answers-notice';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import {
  useInstitutionDetailsLocked,
  useInstitutionDetailsMutation,
  useInstitutionDetailsV2Answers,
  useInstitutionDetailsVersion,
} from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import {
  INSTITUTION_REGULATORY_INFORMATION_DEFAULTS,
  type InstitutionRegulatoryInformation,
  type InstitutionRegulatoryInformationOrDefaults,
  institutionDetailsRegulatoryInformationApiToInstitutionRegulatoryInformation,
  institutionRegulatoryInformationHistorySchema,
  institutionRegulatoryInformationStatusSchema,
  institutionRegulatoryInformationToInstitutionDetailsRegulatoryInformationApi,
} from '@/features/institution/regulatory-information/schema';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionRegulatoryInformationFieldsetHistory } from './fieldsets/history';
import { InstitutionRegulatoryInformationFieldsetJurisdictions } from './fieldsets/jurisdictions';
import { InstitutionRegulatoryInformationFieldsetLicence } from './fieldsets/licence';
import { InstitutionRegulatoryInformationFieldsetOffshoreLicence } from './fieldsets/offshore-licence';
import { InstitutionRegulatoryInformationFieldsetSupervision } from './fieldsets/supervision';

const InstitutionRegulatoryInformationFormStepStatusLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.regulatoryInformation.steps.status.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.regulatoryInformation.steps.status.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionRegulatoryInformationFormStepStatusLegend.displayName = 'InstitutionRegulatoryInformationFormStepStatusLegend';

const InstitutionRegulatoryInformationFormStepStatus = () => {
  const form = useFormContext<InstitutionRegulatoryInformationOrDefaults>();
  const { locked } = useInstitutionDetailsLocked();
  const hasRegulatorySupervisionOrOversight = form.watch('hasRegulatorySupervisionOrOversight');

  return (
    <div className="space-y-6">
      {locked.regulatoryInformation ? <LockedAnswersNotice i18nKey="institutionDetails.update.locked" /> : null}
      <InstitutionRegulatoryInformationFieldsetSupervision />
      {hasRegulatorySupervisionOrOversight === 'YES' ? <InstitutionRegulatoryInformationFieldsetLicence /> : null}
      <Separator />
      <InstitutionRegulatoryInformationFieldsetJurisdictions />
    </div>
  );
};
InstitutionRegulatoryInformationFormStepStatus.displayName = 'InstitutionRegulatoryInformationFormStepStatus';

const InstitutionRegulatoryInformationFormStepHistoryLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.regulatoryInformation.steps.history.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.regulatoryInformation.steps.history.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionRegulatoryInformationFormStepHistoryLegend.displayName = 'InstitutionRegulatoryInformationFormStepHistoryLegend';

const InstitutionRegulatoryInformationFormStepHistory = () => (
  <div className="space-y-6">
    <InstitutionRegulatoryInformationFieldsetOffshoreLicence />
    <InstitutionRegulatoryInformationFieldsetHistory />
  </div>
);
InstitutionRegulatoryInformationFormStepHistory.displayName = 'InstitutionRegulatoryInformationFormStepHistory';

const FORM_STEPS = {
  STATUS: {
    Component: InstitutionRegulatoryInformationFormStepStatus,
    Legend: InstitutionRegulatoryInformationFormStepStatusLegend,
    schema: institutionRegulatoryInformationStatusSchema,
  },
  HISTORY: {
    Component: InstitutionRegulatoryInformationFormStepHistory,
    Legend: InstitutionRegulatoryInformationFormStepHistoryLegend,
    schema: institutionRegulatoryInformationHistorySchema,
  },
} satisfies Record<string, FormStep<InstitutionRegulatoryInformation>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionRegulatoryInformationFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionRegulatoryInformation>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'regulatoryInformation', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionRegulatoryInformationFormCurrentStep.displayName = 'InstitutionRegulatoryInformationFormCurrentStep';

const InstitutionRegulatoryInformationFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionRegulatoryInformation>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionRegulatoryInformationFormCurrentStepLegend.displayName = 'InstitutionRegulatoryInformationFormCurrentStepLegend';

const InstitutionRegulatoryInformationFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionRegulatoryInformation>();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('regulatoryInformation');
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(prevDetailsRoute, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionRegulatoryInformationFormBackButton.displayName = 'InstitutionRegulatoryInformationFormBackButton';

const InstitutionRegulatoryInformationFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionRegulatoryInformation>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionRegulatoryInformationFormFooter.displayName = 'InstitutionRegulatoryInformationFormFooter';

const InstitutionRegulatoryInformationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { locked } = useInstitutionDetailsLocked();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'regulatoryInformation');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('regulatoryInformation');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionRegulatoryInformation, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { regulatoryInformation: await institutionRegulatoryInformationToInstitutionDetailsRegulatoryInformationApi(data) };

    await mutateAsync(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
      },
    });
  };

  if (isLoading) return <LoadingCard />;

  // Locked answers win, as they do when the draft is saved
  const defaultValues = {
    ...(institutionDetailsAnswers?.regulatoryInformation
      ? institutionDetailsRegulatoryInformationApiToInstitutionRegulatoryInformation(institutionDetailsAnswers.regulatoryInformation)
      : INSTITUTION_REGULATORY_INFORMATION_DEFAULTS),
    ...locked.regulatoryInformation,
  };

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionRegulatoryInformationFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionRegulatoryInformationFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionRegulatoryInformation) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionRegulatoryInformationFormCurrentStep />
          <InstitutionRegulatoryInformationFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionRegulatoryInformationForm.displayName = 'InstitutionRegulatoryInformationForm';

export default InstitutionRegulatoryInformationForm;
